// Hud.tsx — 2D overlay chrome over the 3D canvas: the title, the game calendar +
// readout (upper-right), and — when a system is focused — the side panel. The
// back control steps out one focus level at a time (planet → system → galaxy).
import { useEffect, useState } from 'react';
import { useGame, useMatch, useSim } from '../state/context';
import { isEmpireShip } from '../state/economy';
import { SPEED_STEPS } from '../state/gameClock';
import type { Galaxy, StarSystem } from '../types';
import type { Player } from '../api';
import { latestPerf } from '../perf';
import { Calendar } from './Calendar';
import { SystemPanel } from './SystemPanel';
import { ShipPane } from './ShipPane';
import { EmpireBar, EmpireShipPane, FleetPanel, GameOver, MatchSpeed, Scoreboard } from './Empire';

// Compact multiplier label: 1, 1k, 10k, 1M.
function speedLabel(s: number): string {
  if (s >= 1_000_000) return `${s / 1_000_000}M`;
  if (s >= 1_000) return `${s / 1_000}k`;
  return `${s}`;
}

// Simulation-speed selector (1×–1,000,000×); drives the shared game clock.
function SpeedControl() {
  const { speed, setSpeed } = useGame();
  return (
    <div className="speed">
      <span className="speed-label">SPEED</span>
      {SPEED_STEPS.map((s) => (
        <button
          key={s}
          type="button"
          className={s === speed ? 'speed-btn active' : 'speed-btn'}
          onClick={() => setSpeed(s)}
        >
          {speedLabel(s)}
        </button>
      ))}
    </div>
  );
}

// Live FPS / dropped-frame readout, refreshed off the shared perf snapshot.
function PerfOverlay() {
  const [, force] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => force((n) => n + 1), 500);
    return () => window.clearInterval(id);
  }, []);
  return (
    <div className="perf">
      {Math.round(latestPerf.fps)} fps · {latestPerf.dropped} drop · p95 {latestPerf.p95Ms.toFixed(0)}ms
    </div>
  );
}

interface Props {
  galaxy: Galaxy;
  focusedSystem: StarSystem | null;
  focusedPlanetId: string | null;
  player: Player | null;
}

export function Hud({ galaxy, focusedSystem, focusedPlanetId, player }: Props) {
  const { focus, focusUp, focusedMoonId, selectedSingularityId, selectedShipId } = useGame();
  const sim = useSim();
  const match = useMatch();
  // A selected ship's info pane takes precedence over the system panel.
  const selectedShip = selectedShipId ? (sim?.ships ?? []).find((s) => s.id === selectedShipId) : null;

  return (
    <div className="hud">
      <header className="hud-top">
        <div className="brand">
          <span className="brand-mark">✦</span> FARSTARS
        </div>
        {match ? <EmpireBar /> : null}
      </header>
      {match ? (
        <div className="hud-left">
          <Scoreboard galaxy={galaxy} />
          <FleetPanel galaxy={galaxy} />
        </div>
      ) : null}
      <div className="hud-bottom-left">
        <Calendar />
        {match ? <MatchSpeed /> : <SpeedControl />}
        <div className="readout">
          {player ? <span className="player">{player.email}</span> : null}
          <span>{galaxy.systems.length} systems</span>
        </div>
        <PerfOverlay />
      </div>
      {selectedShip ? (
        isEmpireShip(selectedShip) ? (
          <EmpireShipPane galaxy={galaxy} ship={selectedShip} />
        ) : (
          <ShipPane galaxy={galaxy} ship={selectedShip} />
        )
      ) : focusedSystem ? (
        <SystemPanel
          galaxy={galaxy}
          system={focusedSystem}
          focusedPlanetId={focusedPlanetId}
          focusedMoonId={focusedMoonId}
          selectedSingularityId={selectedSingularityId}
          onSelectPlanet={focus}
          onSelectMoon={focus}
          onSelectSingularity={focus}
          onTravel={focus}
          onBack={focusUp}
        />
      ) : (
        <footer className="hud-hint">
          drag to orbit · scroll to zoom · arrow keys to pan · click a star to enter its system
        </footer>
      )}
      {match ? <GameOver /> : null}
    </div>
  );
}
